/**
 * SiteTemplateRenderer
 *
 * Live preview renderer for the site page editor.
 * Mirrors the actual Landing.tsx section layouts and styling:
 *   - Dark/light alternating sections
 *   - Playfair Display headings, Montserrat body (via site CSS)
 *   - REDEX vs Honeyshares layout variants
 *
 * Also used by SitePage.tsx for the public /p/:slug route.
 *
 * Composition model (Squarespace-style):
 *   Nav → Template Sections (hero, features…) → Custom Blocks Zone → Footer
 *   Blocks render INSIDE the template shell, never instead of it.
 */
import React, { Suspense } from 'react';
import { BRAND_ASSETS, TENANT } from '../../../config';
import { PageRenderer } from './PageRenderer';
import type { BlockInstance } from './types';

/* ─── Helpers ─────────────────────────────────────────────────── */

type S = Record<string, any>;

function v(sections: S, sid: string, key: string, fallback = ''): string {
  return sections?.[sid]?.[key] ?? fallback;
}

function va(sections: S, sid: string, key: string): any[] {
  const val = sections?.[sid]?.[key];
  if (Array.isArray(val)) return val;
  return [];
}

/* ─── Dark Section wrapper ────────────────────────────────────── */

function DarkSection({ id, children, className = '' }: { id?: string; children: React.ReactNode; className?: string }) {
  return (
    <section id={id} className={`py-12 md:py-20 bg-[hsl(var(--dark-bg))] text-[hsl(var(--dark-foreground))] ${className}`}>
      <div className="container mx-auto px-4">{children}</div>
    </section>
  );
}

function LightSection({ id, children, className = '' }: { id?: string; children: React.ReactNode; className?: string }) {
  return (
    <section id={id} className={`py-12 md:py-20 bg-white ${className}`}>
      <div className="container mx-auto px-4">{children}</div>
    </section>
  );
}

/* ═══════════════════════════════════════════════════════════════
 *  NAV (preview)
 * ═══════════════════════════════════════════════════════════════ */

function NavPreview({ sections }: { sections: S }) {
  const links = va(sections, 'nav', 'links');
  return (
    <header className="sticky top-0 z-10 flex items-center justify-between px-6 py-3 bg-white border-b border-border">
      <div className="flex items-center gap-2">
        <img src={BRAND_ASSETS.logo.primary} alt="" className="h-8 w-auto" />
        <span className="font-semibold text-sm">{TENANT.branding.name}</span>
      </div>
      <nav className="hidden md:flex items-center gap-6 text-sm text-muted-foreground">
        {links.length > 0
          ? links.map((l: any, i: number) => <span key={i} className="hover:text-foreground cursor-default">{l.label}</span>)
          : <>
              <span>Home</span>
              <span>About</span>
              <span>Invest</span>
            </>
        }
      </nav>
      <span className="text-xs px-4 py-1.5 rounded bg-primary text-primary-foreground font-semibold">Get Started</span>
    </header>
  );
}

/* ═══════════════════════════════════════════════════════════════
 *  FOOTER (preview)
 * ═══════════════════════════════════════════════════════════════ */

function FooterPreview({ sections }: { sections: S }) {
  const disclaimer = v(sections, 'footer', 'disclaimer');
  return (
    <footer className="bg-[hsl(var(--dark-bg))] text-[hsl(var(--dark-foreground))] border-t border-white/10 py-8">
      <div className="container mx-auto px-4">
        <div className="flex flex-col md:flex-row justify-between items-center gap-3">
          <div className="flex items-center gap-2">
            <img src={BRAND_ASSETS.logo.allWhite || BRAND_ASSETS.logo.primary} alt="" className="h-5 w-auto" />
            <span className="text-xs text-gray-400">&copy; 2026 {TENANT.branding.name}. All rights reserved.</span>
          </div>
          <div className="flex gap-4 text-xs text-gray-400">
            <span>Terms of Service</span>
            <span>Privacy Policy</span>
          </div>
        </div>
        {disclaimer && <p className="text-[10px] text-gray-500 text-center mt-4 max-w-3xl mx-auto">{disclaimer}</p>}
      </div>
    </footer>
  );
}

/* ═══════════════════════════════════════════════════════════════
 *  REDEX LANDING — mirrors Landing.tsx REDEX branch
 * ═══════════════════════════════════════════════════════════════ */

function RedexLanding({ sections }: { sections: S }) {
  const pillars = va(sections, 'platform', 'pillars');
  const railsCards = va(sections, 'rails', 'cards');
  const invoiceCards = va(sections, 'invoices', 'cards');

  const builtForRaw = v(sections, 'platform', 'builtForTags');
  const builtFor = builtForRaw ? builtForRaw.split(',').map((s: string) => s.trim()).filter(Boolean) : [];

  return (
    <>
      {/* Hero — dark */}
      <DarkSection id="home" className="min-h-[320px] flex items-center">
        <div className="max-w-5xl mx-auto">
          <div className="grid lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-7">
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-medium mb-4 leading-tight">
                {v(sections, 'hero', 'heading', 'American Dynamism Unleashed')}
              </h1>
              <p className="text-sm md:text-base text-gray-400 mb-3 leading-relaxed">
                {v(sections, 'hero', 'subheading', 'Capital infrastructure for builders, operators, and owners.')}
              </p>
              {v(sections, 'hero', 'body') && (
                <p className="text-xs md:text-sm text-gray-400 mb-4 leading-relaxed">{v(sections, 'hero', 'body')}</p>
              )}
              <div className="flex gap-3">
                <span className="text-xs px-4 py-2 rounded bg-primary text-primary-foreground font-semibold">
                  {v(sections, 'hero', 'ctaText', 'REQUEST ACCESS')}
                </span>
                {v(sections, 'hero', 'secondaryCtaText') && (
                  <span className="text-xs px-4 py-2 rounded border border-white/20 text-white">
                    {v(sections, 'hero', 'secondaryCtaText')}
                  </span>
                )}
              </div>
            </div>
            <div className="lg:col-span-5">
              <div className="bg-white/5 border border-white/10 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-4">
                  <img src={BRAND_ASSETS.logo.primary} alt="" className="h-7 w-auto" />
                  <div>
                    <p className="text-[10px] text-gray-400">Capital infrastructure</p>
                    <p className="text-sm font-semibold">{TENANT.branding.name}</p>
                  </div>
                </div>
                <div className="space-y-3">
                  {['Sovereignty', 'Ownership-first', 'Industrial execution', 'Capital velocity'].map(label => (
                    <div key={label} className="flex items-start gap-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-primary mt-1.5 shrink-0" />
                      <p className="text-xs text-gray-300">{label}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </DarkSection>

      {/* Platform — light */}
      <LightSection id="platform">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-8">
            <h2 className="text-2xl md:text-3xl font-bold mb-2 text-foreground">
              {v(sections, 'platform', 'heading', 'Capital sovereignty for builders')}
            </h2>
            <p className="text-sm text-muted-foreground max-w-2xl mx-auto">
              {v(sections, 'platform', 'subheading')}
            </p>
          </div>
          {pillars.length > 0 && (
            <div className="grid md:grid-cols-2 gap-4">
              {pillars.map((p: any, i: number) => (
                <div key={i} className="rounded-xl border border-border bg-card p-4">
                  <h3 className="text-sm font-bold mb-1 text-foreground">{p.title}</h3>
                  <p className="text-xs text-muted-foreground">{p.description}</p>
                </div>
              ))}
            </div>
          )}
          {builtFor.length > 0 && (
            <div className="mt-6 rounded-xl border border-border bg-muted p-4">
              <h3 className="text-sm font-bold text-foreground mb-2">Built for</h3>
              <div className="flex flex-wrap gap-1.5">
                {builtFor.map((t: string) => (
                  <span key={t} className="inline-flex items-center rounded-full bg-background border border-border px-2 py-0.5 text-[10px] text-muted-foreground">{t}</span>
                ))}
              </div>
            </div>
          )}
        </div>
      </LightSection>

      {/* Rails — light */}
      <LightSection id="rails">
        <div className="max-w-5xl mx-auto">
          <div className="grid md:grid-cols-2 gap-8 items-start">
            <div>
              <h2 className="text-2xl md:text-3xl font-bold mb-2 text-foreground">
                {v(sections, 'rails', 'heading', 'Programmable capital rails')}
              </h2>
              <p className="text-sm text-muted-foreground leading-relaxed">
                {v(sections, 'rails', 'body')}
              </p>
            </div>
            <div className="space-y-3">
              {railsCards.map((c: any, i: number) => (
                <div key={i} className="rounded-xl border border-border bg-card p-4">
                  <p className="text-sm font-semibold text-foreground">{c.title}</p>
                  <p className="text-xs text-muted-foreground mt-1">{c.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </LightSection>

      {/* Invoices — dark */}
      <DarkSection id="invoices">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-2xl md:text-3xl font-bold mb-2">
            {v(sections, 'invoices', 'heading', 'Transaction + invoicing infrastructure')}
          </h2>
          <p className="text-sm text-gray-400 mb-6 max-w-2xl mx-auto">
            {v(sections, 'invoices', 'subheading')}
          </p>
          {invoiceCards.length > 0 && (
            <div className="grid md:grid-cols-2 gap-4">
              {invoiceCards.map((c: any, i: number) => (
                <div key={i} className="bg-white/5 border border-white/10 rounded-xl p-4 text-left">
                  <h3 className="text-sm font-bold mb-1">{c.title}</h3>
                  <p className="text-xs text-gray-400">{c.description}</p>
                </div>
              ))}
            </div>
          )}
          <div className="mt-6">
            <span className="text-xs px-6 py-2 rounded bg-primary text-primary-foreground font-semibold">GET STARTED</span>
          </div>
        </div>
      </DarkSection>
    </>
  );
}

/* ═══════════════════════════════════════════════════════════════
 *  HONEYSHARES LANDING — mirrors Landing.tsx Honeyshares branch
 * ═══════════════════════════════════════════════════════════════ */

function HoneysharesLanding({ sections }: { sections: S }) {
  const steps = va(sections, 'howItWorks', 'steps');
  const faqItems = va(sections, 'faq', 'items');

  return (
    <>
      {/* Hero — dark */}
      <DarkSection id="home" className="min-h-[280px] flex items-center"
        {...(v(sections, 'hero', 'backgroundImage') ? {
          style: {
            backgroundImage: `linear-gradient(rgba(0,0,0,0.6), rgba(0,0,0,0.7)), url(${v(sections, 'hero', 'backgroundImage')})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }
        } : {})}
      >
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-medium mb-4 leading-tight">
            {v(sections, 'hero', 'heading', 'Real Estate Investing, Simplified')}
          </h1>
          <p className="text-sm md:text-base text-gray-400 mb-6 max-w-3xl mx-auto">
            {v(sections, 'hero', 'subheading', 'Institutional-grade real estate, simplified for the modern investor')}
          </p>
          <span className="text-xs px-6 py-2 rounded bg-primary text-primary-foreground font-semibold">
            {v(sections, 'hero', 'ctaText', 'Get Started')}
          </span>
        </div>
      </DarkSection>

      {/* How It Works — light */}
      <LightSection id="about">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-bold mb-8 text-center text-foreground">
            {v(sections, 'howItWorks', 'heading', 'How It Works')}
          </h2>
          <div className="grid sm:grid-cols-3 gap-8">
            {(steps.length > 0 ? steps : [
              { title: 'Browse', description: 'Explore curated investments' },
              { title: 'Invest', description: 'Choose properties aligned to your goals' },
              { title: 'Earn', description: 'Receive regular distributions' },
            ]).map((step: any, i: number) => (
              <div key={i} className="text-center">
                <div className="w-14 h-14 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xl font-bold mx-auto mb-3">
                  {i + 1}
                </div>
                <h3 className="text-base font-bold mb-1 text-foreground">{step.title}</h3>
                <p className="text-xs text-muted-foreground">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </LightSection>

      {/* Hive Fund — dark */}
      <DarkSection id="invest">
        <div className="max-w-5xl mx-auto">
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div className="order-2 md:order-1">
              {v(sections, 'hiveFund', 'image') ? (
                <img src={v(sections, 'hiveFund', 'image')} alt="" className="w-full rounded-lg" />
              ) : (
                <div className="w-full aspect-[4/3] bg-white/5 border border-white/10 rounded-lg flex items-center justify-center text-gray-500 text-xs">
                  Fund Image
                </div>
              )}
            </div>
            <div className="order-1 md:order-2">
              <h2 className="text-2xl md:text-3xl font-bold mb-4">
                {v(sections, 'hiveFund', 'heading', 'The Hive Fund')}
              </h2>
              <div className="space-y-2 mb-4">
                <div className="flex items-baseline gap-2">
                  <span className="text-2xl md:text-3xl font-medium text-primary">{v(sections, 'hiveFund', 'targetIrr', '18%+')}</span>
                  <span className="text-sm text-gray-400">target IRR</span>
                </div>
                <div className="flex items-baseline gap-2">
                  <span className="text-2xl md:text-3xl font-medium text-primary">{v(sections, 'hiveFund', 'targetCashflow', '12%+')}</span>
                  <span className="text-sm text-gray-400">target annual cashflow</span>
                </div>
              </div>
              <div className="bg-white/5 border border-white/10 rounded-lg p-3 mb-3">
                <p className="text-[10px] text-gray-400">
                  <span className="font-semibold text-white">Accredited investors only.</span> Minimum investment {v(sections, 'hiveFund', 'minimumInvestment', '$10,000')}
                </p>
              </div>
              {v(sections, 'hiveFund', 'reserves') && (
                <div className="bg-primary/10 border border-primary/20 rounded-lg p-3 mb-4">
                  <p className="text-[10px] font-medium text-white">{v(sections, 'hiveFund', 'reserves')}</p>
                </div>
              )}
              <div className="flex gap-3">
                <span className="text-xs px-4 py-2 rounded bg-primary text-primary-foreground font-semibold">
                  {v(sections, 'hiveFund', 'ctaText', 'Request Access')}
                </span>
                {v(sections, 'hiveFund', 'secondaryCtaText') && (
                  <span className="text-xs px-4 py-2 rounded border border-white/20 text-white">
                    {v(sections, 'hiveFund', 'secondaryCtaText')}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      </DarkSection>

      {/* FAQ — light */}
      <LightSection>
        <div className="max-w-2xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-bold mb-6 text-center text-foreground">
            {v(sections, 'faq', 'heading', 'Frequently Asked Questions')}
          </h2>
          <div className="space-y-2">
            {(faqItems.length > 0 ? faqItems : [
              { question: 'How does investing with Honeyshares work?', answer: 'Eligible investors can access curated property-backed opportunities with professional management and investor reporting.' },
            ]).map((item: any, i: number) => (
              <div key={i} className="border border-border rounded-lg p-3">
                <p className="text-sm font-semibold text-foreground">{item.question}</p>
                <p className="text-xs text-muted-foreground mt-1">{item.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </LightSection>

      {/* Newsletter — dark */}
      <DarkSection>
        <div className="max-w-xl mx-auto text-center">
          <h2 className="text-2xl md:text-3xl font-bold mb-2">
            {v(sections, 'newsletter', 'heading', 'Stay Updated')}
          </h2>
          <p className="text-sm text-gray-400 mb-4">
            {v(sections, 'newsletter', 'subheading', 'Get the latest investment opportunities delivered to your inbox')}
          </p>
          <div className="flex gap-2 max-w-sm mx-auto">
            <span className="flex-1 px-3 py-2 rounded border border-white/10 bg-white/5 text-xs text-gray-500">Enter your email</span>
            <span className="text-xs px-4 py-2 rounded bg-primary text-primary-foreground font-semibold">SUBSCRIBE</span>
          </div>
        </div>
      </DarkSection>
    </>
  );
}

/* ═══════════════════════════════════════════════════════════════
 *  GENERIC / ABOUT / TEAM / FUNNEL / CONTACT previews
 * ═══════════════════════════════════════════════════════════════ */

function GenericPreview({ sections }: { sections: S }) {
  const features = va(sections, 'features', 'items');
  return (
    <>
      <DarkSection className="min-h-[200px] flex items-center">
        <div className="max-w-3xl mx-auto text-center">
          <h1 className="text-3xl md:text-4xl font-bold mb-2">{v(sections, 'hero', 'heading', 'Welcome')}</h1>
          {v(sections, 'hero', 'subheading') && <p className="text-sm text-gray-400">{v(sections, 'hero', 'subheading')}</p>}
        </div>
      </DarkSection>
      {(v(sections, 'content1', 'heading') || v(sections, 'content1', 'body')) && (
        <LightSection>
          <div className="max-w-3xl mx-auto">
            <h2 className="text-2xl font-bold mb-2 text-foreground">{v(sections, 'content1', 'heading')}</h2>
            <p className="text-sm text-muted-foreground">{v(sections, 'content1', 'body')}</p>
          </div>
        </LightSection>
      )}
      {(v(sections, 'content2', 'heading') || v(sections, 'content2', 'body')) && (
        <LightSection>
          <div className="max-w-3xl mx-auto">
            <h2 className="text-2xl font-bold mb-2 text-foreground">{v(sections, 'content2', 'heading')}</h2>
            <p className="text-sm text-muted-foreground">{v(sections, 'content2', 'body')}</p>
          </div>
        </LightSection>
      )}
      {features.length > 0 && (
        <LightSection>
          <div className="max-w-3xl mx-auto">
            <h2 className="text-2xl font-bold mb-4 text-foreground text-center">{v(sections, 'features', 'heading')}</h2>
            <div className="grid sm:grid-cols-2 gap-3">
              {features.map((f: any, i: number) => (
                <div key={i} className="border border-border rounded-lg p-3">
                  <h3 className="text-sm font-semibold text-foreground">{f.title}</h3>
                  <p className="text-xs text-muted-foreground mt-1">{f.description}</p>
                </div>
              ))}
            </div>
          </div>
        </LightSection>
      )}
      {v(sections, 'cta', 'heading') && (
        <DarkSection>
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="text-2xl font-bold mb-2">{v(sections, 'cta', 'heading')}</h2>
            {v(sections, 'cta', 'body') && <p className="text-sm text-gray-400 mb-4">{v(sections, 'cta', 'body')}</p>}
            {v(sections, 'cta', 'ctaText') && (
              <span className="text-xs px-6 py-2 rounded bg-primary text-primary-foreground font-semibold">{v(sections, 'cta', 'ctaText')}</span>
            )}
          </div>
        </DarkSection>
      )}
    </>
  );
}

function AboutPreview({ sections }: { sections: S }) {
  const statements = va(sections, 'empowering', 'statements');
  const features = va(sections, 'features', 'items');

  // Parse body into paragraphs
  const bodyText = v(sections, 'hero', 'body');
  const bodyParagraphs = bodyText ? bodyText.split('\n').filter((s: string) => s.trim()) : [];

  return (
    <>
      {/* Hero — dark */}
      <DarkSection className="min-h-[260px]">
        <div className="grid lg:grid-cols-2 gap-8 items-center max-w-5xl mx-auto">
          <div>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-medium mb-4 leading-tight">
              {v(sections, 'hero', 'heading', 'About Us')}
            </h1>
            <div className="space-y-3 text-sm text-gray-300 leading-relaxed max-w-xl">
              {bodyParagraphs.length > 0
                ? bodyParagraphs.map((p: string, i: number) => <p key={i}>{p}</p>)
                : <p>Our story...</p>
              }
            </div>
            {v(sections, 'hero', 'ctaText') && (
              <div className="mt-4">
                <span className="text-xs px-6 py-2 rounded bg-primary text-primary-foreground font-semibold uppercase tracking-wide">
                  {v(sections, 'hero', 'ctaText')}
                </span>
              </div>
            )}
          </div>
          <div className="hidden lg:block">
            {v(sections, 'hero', 'image') ? (
              <img src={v(sections, 'hero', 'image')} alt="" className="w-full h-[300px] object-cover rounded-xl" />
            ) : (
              <div className="w-full h-[300px] bg-white/5 border border-white/10 rounded-xl flex items-center justify-center text-gray-500 text-xs">Hero Image</div>
            )}
          </div>
        </div>
      </DarkSection>

      {/* Empowering — light */}
      <LightSection>
        <div className="grid lg:grid-cols-2 gap-8 items-center max-w-5xl mx-auto">
          <div>
            <h2 className="text-2xl md:text-3xl font-serif font-medium text-foreground leading-tight">
              {v(sections, 'empowering', 'heading', 'Empowering investors')}
            </h2>
          </div>
          <div className="space-y-3 text-sm text-muted-foreground leading-relaxed">
            {statements.length > 0
              ? statements.map((s: any, i: number) => <p key={i}>{s.description || s.title}</p>)
              : <p>Our mission is to make high-quality real estate investing simple and accessible.</p>
            }
          </div>
        </div>
      </LightSection>

      {/* Strategy — dark */}
      <DarkSection>
        <div className="grid lg:grid-cols-2 gap-8 items-center max-w-5xl mx-auto">
          <div className="flex justify-center">
            {v(sections, 'strategy', 'image') ? (
              <img src={v(sections, 'strategy', 'image')} alt="" className="max-h-[280px] object-contain drop-shadow-2xl" />
            ) : (
              <div className="w-48 h-48 bg-white/5 border border-white/10 rounded-lg flex items-center justify-center text-gray-500 text-xs">Image</div>
            )}
          </div>
          <div>
            <h2 className="text-xl md:text-2xl font-bold mb-3 leading-tight">
              {v(sections, 'strategy', 'heading', 'We target properties with exceptional potential.')}
            </h2>
            <p className="text-sm text-gray-400 leading-relaxed">
              {v(sections, 'strategy', 'body')}
            </p>
          </div>
        </div>
      </DarkSection>

      {/* Feature Cards — dark */}
      {features.length > 0 && (
        <DarkSection>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 max-w-5xl mx-auto">
            {features.map((card: any, i: number) => (
              <div key={i} className="bg-white/5 border border-white/10 rounded-xl p-4 hover:bg-white/10 transition-colors">
                <h3 className="text-sm font-bold text-white mb-2">{card.title}</h3>
                <p className="text-xs text-gray-400 leading-relaxed">{card.description}</p>
              </div>
            ))}
          </div>
        </DarkSection>
      )}

      {/* Newsletter */}
      <section className="py-8 bg-primary text-primary-foreground">
        <div className="container mx-auto px-4">
          <div className="max-w-xl">
            <h2 className="text-xl font-serif font-medium mb-2">
              {v(sections, 'newsletter', 'heading', 'Join our List')}
            </h2>
            <p className="text-xs opacity-90 mb-3 max-w-md">
              {v(sections, 'newsletter', 'subheading')}
            </p>
            <div className="flex gap-2 max-w-md">
              <span className="flex-1 px-3 py-2 rounded border border-black/20 bg-white text-xs text-gray-400">Email Address</span>
              <span className="text-xs px-4 py-2 rounded bg-black text-white font-semibold">SIGN UP</span>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

function TeamPreview({ sections }: { sections: S }) {
  const leaders = va(sections, 'leadership', 'members');
  const team = va(sections, 'team', 'members');
  const renderMembers = (members: any[]) => (
    <div className="grid sm:grid-cols-3 gap-3">
      {members.map((m: any, i: number) => (
        <div key={i} className="border border-border rounded-lg p-3 text-center">
          {m.image ? (
            <img src={m.image} alt="" className="w-14 h-14 rounded-full mx-auto mb-2 object-cover" />
          ) : (
            <div className="w-14 h-14 rounded-full bg-muted mx-auto mb-2 flex items-center justify-center text-xs text-muted-foreground">
              {(m.name || '?')[0]}
            </div>
          )}
          <h3 className="text-sm font-semibold text-foreground">{m.name}</h3>
          <p className="text-xs text-muted-foreground">{m.role}</p>
        </div>
      ))}
    </div>
  );
  return (
    <>
      <DarkSection className="min-h-[180px] flex items-center">
        <div className="max-w-3xl mx-auto text-center">
          <h1 className="text-3xl font-bold mb-2">{v(sections, 'hero', 'heading', 'Our Team')}</h1>
          {v(sections, 'hero', 'subheading') && <p className="text-sm text-gray-400">{v(sections, 'hero', 'subheading')}</p>}
        </div>
      </DarkSection>
      {leaders.length > 0 && (
        <LightSection>
          <div className="max-w-3xl mx-auto">
            <h2 className="text-2xl font-bold mb-4 text-foreground text-center">{v(sections, 'leadership', 'heading', 'Leadership')}</h2>
            {renderMembers(leaders)}
          </div>
        </LightSection>
      )}
      {team.length > 0 && (
        <LightSection>
          <div className="max-w-3xl mx-auto">
            <h2 className="text-2xl font-bold mb-4 text-foreground text-center">{v(sections, 'team', 'heading', 'The Team')}</h2>
            {renderMembers(team)}
          </div>
        </LightSection>
      )}
      {v(sections, 'cta', 'heading') && (
        <DarkSection>
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="text-2xl font-bold mb-2">{v(sections, 'cta', 'heading')}</h2>
            {v(sections, 'cta', 'body') && <p className="text-sm text-gray-400 mb-4">{v(sections, 'cta', 'body')}</p>}
            <span className="text-xs px-6 py-2 rounded bg-primary text-primary-foreground font-semibold">{v(sections, 'cta', 'ctaText', 'Learn More')}</span>
          </div>
        </DarkSection>
      )}
    </>
  );
}

function FunnelPreview({ sections }: { sections: S }) {
  const benefits = va(sections, 'benefits', 'items');
  const stats = va(sections, 'socialProof', 'stats');
  return (
    <>
      <DarkSection className="min-h-[240px] flex items-center">
        <div className="max-w-3xl mx-auto text-center">
          <h1 className="text-3xl md:text-4xl font-bold mb-2">{v(sections, 'hero', 'heading', 'Exclusive Opportunity')}</h1>
          {v(sections, 'hero', 'subheading') && <p className="text-sm text-gray-400 mb-4">{v(sections, 'hero', 'subheading')}</p>}
          <span className="text-xs px-6 py-2 rounded bg-primary text-primary-foreground font-semibold">{v(sections, 'hero', 'ctaText', 'Get Early Access')}</span>
        </div>
      </DarkSection>
      {benefits.length > 0 && (
        <LightSection>
          <div className="max-w-3xl mx-auto">
            <h2 className="text-2xl font-bold mb-4 text-foreground text-center">{v(sections, 'benefits', 'heading')}</h2>
            <div className="grid sm:grid-cols-2 gap-3">
              {benefits.map((b: any, i: number) => (
                <div key={i} className="border border-border rounded-lg p-3">
                  <h3 className="text-sm font-semibold text-foreground">{b.title}</h3>
                  <p className="text-xs text-muted-foreground mt-1">{b.description}</p>
                </div>
              ))}
            </div>
          </div>
        </LightSection>
      )}
      {stats.length > 0 && (
        <DarkSection>
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="text-2xl font-bold mb-4">{v(sections, 'socialProof', 'heading')}</h2>
            <div className="grid grid-cols-2 gap-4">
              {stats.map((s: any, i: number) => (
                <div key={i}>
                  <p className="text-2xl font-bold text-primary">{s.value}</p>
                  <p className="text-xs text-gray-400">{s.label}</p>
                </div>
              ))}
            </div>
          </div>
        </DarkSection>
      )}
      {v(sections, 'urgency', 'heading') && (
        <LightSection>
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="text-2xl font-bold mb-2 text-foreground">{v(sections, 'urgency', 'heading')}</h2>
            {v(sections, 'urgency', 'body') && <p className="text-sm text-muted-foreground mb-4">{v(sections, 'urgency', 'body')}</p>}
            <span className="text-xs px-6 py-2 rounded bg-primary text-primary-foreground font-semibold">{v(sections, 'urgency', 'ctaText', 'Reserve Your Spot')}</span>
          </div>
        </LightSection>
      )}
    </>
  );
}

function ContactPreview({ sections }: { sections: S }) {
  return (
    <>
      <DarkSection className="min-h-[180px] flex items-center">
        <div className="max-w-3xl mx-auto text-center">
          <h1 className="text-3xl font-bold mb-2">{v(sections, 'hero', 'heading', 'Get In Touch')}</h1>
          {v(sections, 'hero', 'subheading') && <p className="text-sm text-gray-400">{v(sections, 'hero', 'subheading')}</p>}
        </div>
      </DarkSection>
      <LightSection>
        <div className="max-w-xl mx-auto grid sm:grid-cols-3 gap-3">
          {v(sections, 'contactInfo', 'email') && (
            <div className="border border-border rounded-lg p-3 text-center">
              <p className="text-xs text-muted-foreground mb-1">Email</p>
              <p className="text-sm font-medium text-foreground">{v(sections, 'contactInfo', 'email')}</p>
            </div>
          )}
          {v(sections, 'contactInfo', 'phone') && (
            <div className="border border-border rounded-lg p-3 text-center">
              <p className="text-xs text-muted-foreground mb-1">Phone</p>
              <p className="text-sm font-medium text-foreground">{v(sections, 'contactInfo', 'phone')}</p>
            </div>
          )}
          {v(sections, 'contactInfo', 'address') && (
            <div className="border border-border rounded-lg p-3 text-center">
              <p className="text-xs text-muted-foreground mb-1">Address</p>
              <p className="text-sm font-medium text-foreground">{v(sections, 'contactInfo', 'address')}</p>
            </div>
          )}
        </div>
      </LightSection>
    </>
  );
}

/* ═══════════════════════════════════════════════════════════════
 *  HIVE FUND PREVIEW — mirrors HiveFund.tsx
 * ═══════════════════════════════════════════════════════════════ */

function HiveFundPreview({ sections }: { sections: S }) {
  const strategyPoints = va(sections, 'about', 'strategyPoints');
  const marketItems = va(sections, 'markets', 'items');
  const whyWeWinItems = va(sections, 'whyWeWin', 'items');
  const benefitItems = va(sections, 'benefits', 'items');
  const keyTermItems = va(sections, 'keyTerms', 'items');
  const steps = va(sections, 'howItWorks', 'steps');

  // Parse body into paragraphs
  const aboutBody = v(sections, 'about', 'body');
  const aboutParagraphs = aboutBody ? aboutBody.split('\n').filter((s: string) => s.trim()) : [];

  return (
    <>
      {/* Hero — dark gradient */}
      <DarkSection id="hero" className="min-h-[300px] flex items-center bg-gradient-to-b from-[#1A1A1A] to-[#121212]">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-medium mb-3">
            {v(sections, 'hero', 'heading', 'The Hive Fund')}
          </h1>
          <p className="text-sm md:text-base text-white/90 mb-2 max-w-3xl mx-auto">
            {v(sections, 'hero', 'subheading')}
          </p>
          {v(sections, 'hero', 'body') && (
            <p className="text-xs text-white/80 mb-4 max-w-2xl mx-auto">{v(sections, 'hero', 'body')}</p>
          )}
          <div className="flex gap-3 justify-center">
            <span className="text-xs px-4 py-2 rounded bg-primary text-primary-foreground font-semibold">
              {v(sections, 'hero', 'ctaText', 'Request Access')}
            </span>
            {v(sections, 'hero', 'secondaryCtaText') && (
              <span className="text-xs px-4 py-2 rounded border-2 border-primary text-primary">
                {v(sections, 'hero', 'secondaryCtaText')}
              </span>
            )}
          </div>
        </div>
      </DarkSection>

      {/* Summary — light */}
      <LightSection>
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl font-bold text-foreground mb-4">
            {v(sections, 'about', 'heading', 'About The Hive Fund')}
          </h2>
          <div className="space-y-3 text-sm text-muted-foreground leading-relaxed">
            {aboutParagraphs.length > 0
              ? aboutParagraphs.map((p: string, i: number) => <p key={i}>{p}</p>)
              : <p>Fund overview...</p>
            }
          </div>

          {/* Strategy */}
          {v(sections, 'about', 'strategyHeading') && (
            <div className="mt-8">
              <h3 className="text-xl font-bold text-foreground mb-3">{v(sections, 'about', 'strategyHeading')}</h3>
              {v(sections, 'about', 'strategyIntro') && (
                <p className="text-sm text-muted-foreground mb-3">{v(sections, 'about', 'strategyIntro')}</p>
              )}
              {strategyPoints.length > 0 && (
                <ul className="space-y-2 ml-4">
                  {strategyPoints.map((item: any, i: number) => (
                    <li key={i} className="flex items-start gap-2">
                      <span className="text-foreground mt-0.5">✓</span>
                      <span className="text-xs text-muted-foreground">{item.description || item.title}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {/* Markets */}
          {marketItems.length > 0 && (
            <div className="mt-8">
              <h3 className="text-xl font-bold text-foreground mb-3">{v(sections, 'markets', 'heading', 'Markets')}</h3>
              {v(sections, 'markets', 'body') && (
                <p className="text-sm text-muted-foreground mb-3">{v(sections, 'markets', 'body')}</p>
              )}
              <div className="grid grid-cols-2 gap-3">
                {marketItems.map((m: any, i: number) => (
                  <div key={i} className="border border-border rounded-lg p-3">
                    <h4 className="text-sm font-semibold text-foreground mb-1">{m.title}</h4>
                    <p className="text-xs text-muted-foreground">{m.description}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Why We Win */}
          {whyWeWinItems.length > 0 && (
            <div className="mt-8">
              <h3 className="text-xl font-bold text-foreground mb-3">{v(sections, 'whyWeWin', 'heading', 'Why We Win')}</h3>
              {v(sections, 'whyWeWin', 'body') && (
                <p className="text-sm text-muted-foreground mb-3">{v(sections, 'whyWeWin', 'body')}</p>
              )}
              <div className="grid sm:grid-cols-3 gap-3">
                {whyWeWinItems.map((item: any, i: number) => (
                  <div key={i} className="border border-border rounded-lg p-3">
                    <h4 className="text-sm font-bold text-foreground mb-1">{item.title}</h4>
                    <p className="text-xs text-muted-foreground">{item.description}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </LightSection>

      {/* Benefits — light */}
      {benefitItems.length > 0 && (
        <LightSection>
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-6">
              <h2 className="text-2xl font-bold text-foreground mb-2">{v(sections, 'benefits', 'heading', 'Benefits Overview')}</h2>
              {v(sections, 'benefits', 'subheading') && (
                <p className="text-sm text-muted-foreground">{v(sections, 'benefits', 'subheading')}</p>
              )}
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              {benefitItems.map((b: any, i: number) => (
                <div key={i} className="border border-border rounded-lg p-4 relative">
                  <div className="absolute top-3 right-3 text-3xl font-medium text-primary">{i + 1}</div>
                  <h3 className="text-sm font-bold text-foreground mb-1">{b.title}</h3>
                  <p className="text-xs text-muted-foreground">{b.description}</p>
                </div>
              ))}
            </div>

            {/* Key Terms */}
            {keyTermItems.length > 0 && (
              <div className="mt-6 border border-border rounded-lg p-4">
                <h3 className="text-lg font-bold text-foreground mb-3">{v(sections, 'keyTerms', 'heading', 'Key Investment Terms')}</h3>
                <div className="grid grid-cols-4 gap-3">
                  {keyTermItems.map((t: any, i: number) => (
                    <div key={i}>
                      <p className="text-xs text-muted-foreground mb-1">{t.label}</p>
                      <p className="text-xl font-medium text-primary">{t.value}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </LightSection>
      )}

      {/* How It Works — muted bg */}
      {steps.length > 0 && (
        <LightSection className="bg-muted/30">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-6">
              <h2 className="text-2xl font-bold text-foreground mb-2">{v(sections, 'howItWorks', 'heading', 'How It Works')}</h2>
              {v(sections, 'howItWorks', 'subheading') && (
                <p className="text-sm text-muted-foreground">{v(sections, 'howItWorks', 'subheading')}</p>
              )}
            </div>
            <div className="grid sm:grid-cols-4 gap-4">
              {steps.map((step: any, i: number) => (
                <div key={i} className="border border-border bg-card rounded-lg p-4 text-center">
                  <div className="text-3xl font-medium text-primary mb-2">{i + 1}</div>
                  <h3 className="text-sm font-bold text-foreground mb-1">{step.title}</h3>
                  <p className="text-xs text-muted-foreground">{step.description}</p>
                </div>
              ))}
            </div>
          </div>
        </LightSection>
      )}

      {/* CTA — primary bg */}
      {v(sections, 'cta', 'heading') && (
        <section className="py-8 bg-primary text-primary-foreground">
          <div className="container mx-auto px-4 text-center max-w-3xl">
            <h2 className="text-2xl font-bold mb-2">{v(sections, 'cta', 'heading')}</h2>
            {v(sections, 'cta', 'body') && (
              <p className="text-sm opacity-80 mb-4">{v(sections, 'cta', 'body')}</p>
            )}
            <span className="text-xs px-6 py-2 rounded bg-[#242424] text-white font-semibold">
              {v(sections, 'cta', 'ctaText', 'Request Access')}
            </span>
          </div>
        </section>
      )}
    </>
  );
}

/* ═══════════════════════════════════════════════════════════════
 *  MAIN EXPORT
 * ═══════════════════════════════════════════════════════════════ */

interface Props {
  templateId: string;
  sections: Record<string, any>;
  /** Custom blocks to render in the content zone between template sections and footer */
  blocks?: BlockInstance[];
  /** PageRenderer settings for the custom blocks zone */
  blockSettings?: Record<string, any>;
  isPreview?: boolean;
  isEditing?: boolean;
  showNav?: boolean;
  showFooter?: boolean;
  /** Called when user clicks a block in the preview canvas */
  onSelectBlock?: (blockId: string) => void;
  selectedBlockId?: string | null;
}

const SiteTemplateRenderer: React.FC<Props> = ({
  templateId,
  sections = {},
  blocks,
  blockSettings,
  isPreview = false,
  isEditing = false,
  showNav = true,
  showFooter = true,
  onSelectBlock,
  selectedBlockId,
}) => {
  const isRedex = TENANT.id === 'redex';

  let body: React.ReactNode;
  switch (templateId) {
    case 'landing':
      body = isRedex ? <RedexLanding sections={sections} /> : <HoneysharesLanding sections={sections} />;
      break;
    case 'hivefund':
      body = <HiveFundPreview sections={sections} />;
      break;
    case 'about':
      body = <AboutPreview sections={sections} />;
      break;
    case 'team':
      body = <TeamPreview sections={sections} />;
      break;
    case 'funnel':
      body = <FunnelPreview sections={sections} />;
      break;
    case 'contact':
      body = <ContactPreview sections={sections} />;
      break;
    default:
      body = <GenericPreview sections={sections} />;
  }

  const visibleBlocks = (blocks ?? []).filter(b => b.visible);

  // Apply brand style overrides via CSS variable inheritance
  const brandStyles = blockSettings?.brandStyles as { primaryHsl?: string; borderRadius?: string } | undefined;
  const brandVars: React.CSSProperties = brandStyles ? {
    ...(brandStyles.primaryHsl ? { '--primary': brandStyles.primaryHsl } as React.CSSProperties : {}),
    ...(brandStyles.borderRadius ? { '--radius': `${brandStyles.borderRadius}rem` } as React.CSSProperties : {}),
  } : {};

  return (
    <div className={isPreview ? 'text-[85%]' : ''} style={Object.keys(brandVars).length ? brandVars : undefined}>
      {showNav && <NavPreview sections={sections} />}
      {body}

      {/* ── Custom Blocks Zone ─────────────────────────────────────────
          Blocks render here, inside the template shell (nav + footer
          remain). This is the Squarespace composition model: template
          provides global chrome; blocks fill the content area below.
      ──────────────────────────────────────────────────────────────── */}
      {visibleBlocks.length > 0 && (
        <div className="bg-background">
          {isEditing && onSelectBlock ? (
            visibleBlocks.map(block => (
              <div
                key={block.id}
                className={`relative group cursor-pointer outline-none transition-shadow ${
                  selectedBlockId === block.id
                    ? 'ring-2 ring-inset ring-primary'
                    : 'hover:ring-2 hover:ring-inset hover:ring-primary/40'
                }`}
                onClick={() => onSelectBlock(block.id)}
                role="button"
                tabIndex={-1}
              >
                <div className={`absolute top-2 right-2 z-20 text-[10px] font-medium px-1.5 py-0.5 rounded pointer-events-none transition-opacity ${
                  selectedBlockId === block.id
                    ? 'bg-primary text-primary-foreground opacity-100'
                    : 'bg-black/60 text-white opacity-0 group-hover:opacity-100'
                }`}>
                  {selectedBlockId === block.id ? 'Editing' : 'Click to edit'}
                </div>
                <Suspense fallback={<div className="h-16 animate-pulse bg-muted/30" />}>
                  <PageRenderer config={{ blocks: [block], settings: blockSettings ?? {} }} isEditing={false} />
                </Suspense>
              </div>
            ))
          ) : (
            <Suspense fallback={<div className="h-24 animate-pulse bg-muted/40" />}>
              <PageRenderer
                config={{ blocks: visibleBlocks, settings: blockSettings ?? {} }}
                isEditing={isEditing}
              />
            </Suspense>
          )}
        </div>
      )}

      {/* Empty blocks hint — editor only */}
      {isEditing && (blocks ?? []).length === 0 && (
        <div className="py-10 text-center text-muted-foreground text-sm border-t border-dashed border-border mx-8 my-4">
          Custom blocks will appear here, below the template sections.
        </div>
      )}

      {showFooter && <FooterPreview sections={sections} />}
    </div>
  );
};

export default SiteTemplateRenderer;
